import { useCallback, useEffect, useState } from 'react';
import { StudentHistoryService } from '../../../services/StudentHistoryService';
import type { StudentHistoryMessage, StudentHistoryPagination, StudentHistoryStudent } from '../../../types/studentHistory';

interface UseStudentHistoryMessagesResult {
  student: StudentHistoryStudent | null;
  messages: StudentHistoryMessage[];
  pagination: StudentHistoryPagination;
  loading: boolean;
  error: string;
  refresh: () => Promise<void>;
  loadOlderMessages: () => Promise<void>;
}

const INITIAL_PAGINATION: StudentHistoryPagination = {
  hasNextPage: false,
  cursor: null,
};

export const useStudentHistoryMessages = (studentId: number): UseStudentHistoryMessagesResult => {
  const [student, setStudent] = useState<StudentHistoryStudent | null>(null);
  const [messages, setMessages] = useState<StudentHistoryMessage[]>([]);
  const [pagination, setPagination] = useState<StudentHistoryPagination>(INITIAL_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchData = useCallback(async () => {
    if (!Number.isFinite(studentId) || studentId <= 0) {
      setStudent(null);
      setMessages([]);
      setPagination(INITIAL_PAGINATION);
      setLoading(false);
      setError('');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await StudentHistoryService.getStudentHistory(studentId, 300);
      setStudent(response.student);
      setMessages(response.messages);
      setPagination(response.pagination);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro ao carregar histórico da aluna.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const loadOlderMessages = useCallback(async () => {
    return Promise.resolve();
  }, []);

  return {
    student,
    messages,
    pagination,
    loading,
    error,
    refresh: fetchData,
    loadOlderMessages,
  };
};
